import React from 'react';
import { useTreasury } from '../contexts/TreasuryContext';
import { DollarSign, TrendingUp, Shield, Activity } from 'lucide-react';

const Treasury: React.FC = () => {
  const { totalValue, suiStaked, usdcReserve, apy, loading, refresh } = useTreasury();

  const allocationData = [
    { label: 'SUI Staking', value: suiStaked, percentage: 65, color: 'from-cyan-500 to-blue-500' },
    { label: 'USDC Operations', value: usdcReserve, percentage: 35, color: 'from-green-500 to-emerald-500' },
  ];

  const treasuryMetrics = [
    { label: 'Total Value', value: `${(totalValue / 1000000).toFixed(1)}M SUI`, icon: DollarSign },
    { label: 'Current APY', value: `${apy}%`, icon: TrendingUp },
    { label: 'Security Score', value: '98.5%', icon: Shield },
    { label: 'Last Updated', value: '2 min ago', icon: Activity },
  ];

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Treasury Management</h1>
            <p className="text-slate-400">Real-time treasury overview and allocation</p>
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh Data'}
          </button>
        </div>

        {/* Treasury Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {treasuryMetrics.map((metric, index) => (
            <div key={index} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <metric.icon className="w-8 h-8 text-cyan-400" />
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
              <div className="text-2xl font-bold text-white mb-1">{metric.value}</div>
              <div className="text-slate-400 text-sm">{metric.label}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Allocation Chart */}
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-6">Treasury Allocation</h2>
            <div className="space-y-6">
              {allocationData.map((item, index) => (
                <div key={index}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-400">{item.label}</span>
                    <span className="text-white font-medium">
                      ${(item.value / 1000000).toFixed(1)}M ({item.percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-3">
                    <div 
                      className="h-3 rounded-full transition-all duration-300 bg-gradient-to-r from-cyan-500 to-blue-500"
                      style={{ width: `${item.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Performance Chart */}
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-6">Performance Overview</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-slate-700/50 rounded-lg">
                <span className="text-slate-400">Monthly Yield</span>
                <span className="text-green-400 font-semibold">+$45,230</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-slate-700/50 rounded-lg">
                <span className="text-slate-400">Racing Revenue</span>
                <span className="text-blue-400 font-semibold">+$12,450</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-slate-700/50 rounded-lg">
                <span className="text-slate-400">Staking Rewards</span>
                <span className="text-cyan-400 font-semibold">+$32,780</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-slate-700/50 rounded-lg">
                <span className="text-slate-400">Total Monthly</span>
                <span className="text-white font-bold text-lg">+$57,680</span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="mt-8 bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
          <h2 className="text-xl font-semibold text-white mb-6">Recent Treasury Activity</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left text-slate-400 py-3">Transaction</th>
                  <th className="text-left text-slate-400 py-3">Amount</th>
                  <th className="text-left text-slate-400 py-3">Type</th>
                  <th className="text-left text-slate-400 py-3">Time</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-700/50">
                  <td className="py-3 text-white">Staking Rewards</td>
                  <td className="py-3 text-green-400">+$32,780</td>
                  <td className="py-3 text-blue-400">Yield</td>
                  <td className="py-3 text-slate-400">2 hours ago</td>
                </tr>
                <tr className="border-b border-slate-700/50">
                  <td className="py-3 text-white">Racing Win - Thunder Bay</td>
                  <td className="py-3 text-green-400">+$12,450</td>
                  <td className="py-3 text-purple-400">Racing</td>
                  <td className="py-3 text-slate-400">1 day ago</td>
                </tr>
                <tr className="border-b border-slate-700/50">
                  <td className="py-3 text-white">Treasury Rebalance</td>
                  <td className="py-3 text-slate-400">$0</td>
                  <td className="py-3 text-cyan-400">Management</td>
                  <td className="py-3 text-slate-400">3 days ago</td>
                </tr>
                <tr>
                  <td className="py-3 text-white">Member Deposit</td>
                  <td className="py-3 text-green-400">+$1,000</td>
                  <td className="py-3 text-yellow-400">Deposit</td>
                  <td className="py-3 text-slate-400">1 week ago</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Treasury;
